import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type {
	AiTongueKissQualityId,
	AiTongueKissTemplateId,
} from "./ai-tongue-kiss-preset";

/**
 * /ai-tongue-kiss-generator 样片区的片子；第一条同时是工作台右栏唯一的 Example。
 *
 * 全是本页工作台用预设出的自产片（竞品样片几乎看不到舌头，一条不收），onSite true，标档位，「Try this example」回填原图。
 * 来源和压制参数记在 packages/scripts/ai-tongue-kiss-showcase/README.md。
 *
 * R2 `home/showcase/ai-tongue-kiss/` 下 clips/{id}-720p.mp4、posters/{id}-720p.jpg、refs/{ref}.jpg；
 * 右栏和样片区用同一份 720p 文件。清单为空时样片区整个不渲染。
 */
export interface AiTongueKissShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 出这条片用的画质档（只有自产片有），拼贴 hover 时显示档位名 */
	quality?: AiTongueKissQualityId;
	/** 出这条片用的模板；「Try this example」顺带切过去 */
	template?: AiTongueKissTemplateId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 原图（一张合照），「Try this example」填进工作台的槽 */
	inputImages?: string[];
}

const R2_BASE = "home/showcase/ai-tongue-kiss";

/**
 * 视频和 poster 地址的版本参数（assets 域边缘缓存一天，连 404 也缓存）；换片子时加一。
 * 从 2 起：上传前本地 dev 已经请求过 v=1，边缘缓存了 404
 */
const CLIP_VERSION = 2;

type Spec = Omit<
	AiTongueKissShowcaseClip,
	"videoUrl" | "poster" | "inputImages"
> & {
	/** 自产片的原图（refs/ 下的文件名，或别的页面 refs 的完整 R2 路径） */
	refs?: string[];
};

/**
 * 顺序即拼贴顺序，第一条是右栏 Example（舌尖相碰最清楚的沙发那条）。全竖版。
 * 2026-10-31 本页 HD 出（Seedance 2.0 Mini 720p 5 秒 9:16，41 积分），原图是 gpt-image-2 虚构情侣合照
 */
const SPECS: Spec[] = [
	{
		id: "tongue-sofa-couple",
		title: "Cozy Sofa Tongue Kiss at Home",
		description:
			"A couple photo on a sofa under fairy lights becomes a tender tongue kiss: a shy smile, a playful touch of the tongues, then a slow, deep kiss.",
		quality: "hd",
		template: "tongue",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["sofa-couple"],
	},
	{
		id: "tongue-neon-couple",
		title: "Neon Street Tongue Kiss at Night",
		description:
			"A couple photo on a neon city street becomes a tongue kiss: they lock eyes, lean in, the tips of their tongues touch, then they melt into a slow, deep kiss.",
		quality: "hd",
		template: "tongue",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["neon-couple"],
	},
	{
		id: "tongue-beach-couple",
		title: "Sunset Beach Tongue Kiss",
		description:
			"A beach photo at sunset becomes a playful tongue kiss: their tongues meet in the gap between their lips before a long, deep kiss in the warm light.",
		quality: "hd",
		template: "tongue",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["beach-couple"],
	},
];

const refUrl = (ref: string) =>
	buildR2AssetsImageUrl(
		ref.startsWith("home/") ? ref : `${R2_BASE}/refs/${ref}.jpg`,
	);

export const aiTongueKissShowcaseClips: AiTongueKissShowcaseClip[] = SPECS.map(
	({ refs, ...spec }) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}-720p.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(
			`${R2_BASE}/posters/${spec.id}-720p.jpg?v=${CLIP_VERSION}`,
		),
		inputImages: refs?.map(refUrl),
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const AI_TONGUE_KISS_SHOWCASE_UPLOADED_AT = "2026-10-31T00:00:00+08:00";
